import React, { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Box,
  Flex,
  Center,
  Alert,
  AlertIcon,
  useBreakpointValue,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  IconButton,
  ButtonGroup,
  Tooltip,
  Text,
  Spinner,
  Button,
  VStack,
  HStack,
  useToast,
} from '@chakra-ui/react'
import { FiFileText, FiAlignLeft, FiShare2 } from 'react-icons/fi'
import { FileItem, RelatedFileEntry, FileLinksResponse } from '@/types'
import { useFileData } from '@/hooks'
import apiService from '@/services/api'
import PreviewTopBar from './PreviewTopBar'
import PreviewSidebar from './PreviewSidebar'
import PDFViewer from './PDFViewer'
import ImageViewer from './ImageViewer'
import TagManager from './TagManager'
import TextSearchViewer from './TextSearchViewer'
import SpreadsheetViewer from './SpreadsheetViewer'
import DocxViewer from './DocxViewer'
import { useAppTranslation } from '@/contexts/LanguageContext'
import LinkDocumentModal from './LinkDocumentModal'
import LinkGraphView from './LinkGraph/LinkGraphView'

function formatRelevanceScore(score: number): string {
  if (score > 0 && score <= 1) {
    return `${Math.round(score * 100)}%`
  }
  return score.toFixed(4)
}

interface FilePreviewProps {
  file: FileItem
  onBack?: () => void
  onFileRenamed?: (file: FileItem) => void
}

export default function FilePreview({ file, onBack, onFileRenamed }: FilePreviewProps) {
  const { tags } = useFileData(file)
  const { t } = useAppTranslation('files')
  const toast = useToast()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [scale, setScale] = useState(1.0)
  const [localTags, setLocalTags] = useState<string[]>([])
  const isMobile = useBreakpointValue({ base: true, lg: false })
  const { isOpen: isTagModalOpen, onOpen: onTagModalOpen, onClose: onTagModalClose } = useDisclosure()
  const { isOpen: isRelatedOpen, onOpen: onRelatedOpenBase, onClose: onRelatedClose } = useDisclosure()
  const { isOpen: isLinkOpen, onOpen: onLinkOpen, onClose: onLinkClose } = useDisclosure()
  const [relatedItems, setRelatedItems] = useState<RelatedFileEntry[]>([])
  const [relatedLoading, setRelatedLoading] = useState(false)
  const [relatedLoadingMore, setRelatedLoadingMore] = useState(false)
  const [relatedHasMore, setRelatedHasMore] = useState(false)
  const [relatedError, setRelatedError] = useState<string | null>(null)
  const [fileLinks, setFileLinks] = useState<FileLinksResponse>({ outgoing: [], incoming: [] })
  const [linkingRelatedId, setLinkingRelatedId] = useState<number | null>(null)

  const linkedPeerFileIds = React.useMemo(() => {
    const ids = new Set<number>()
    for (const row of fileLinks.outgoing) {
      ids.add(row.file.id)
    }
    for (const row of fileLinks.incoming) {
      ids.add(row.file.id)
    }
    return ids
  }, [fileLinks])

  React.useEffect(() => {
    const trackView = async () => {
      try {
        await apiService.trackFileView(file.id)
      } catch {}
    }
    trackView()
  }, [file.id])

  React.useEffect(() => {
    setLocalTags(tags)
  }, [tags])

  const handleBack = () => {
    if (onBack) onBack()
  }

  const handleTagAdded = (tagName: string) => setLocalTags((prev) => [...prev, tagName])
  const handleTagRemoved = (tagName: string) => setLocalTags((prev) => prev.filter((t) => t !== tagName))

  const [viewMode, setViewMode] = useState<'document' | 'text' | 'graph'>('document')

  React.useEffect(() => {
    setViewMode('document')
  }, [file.id])

  React.useEffect(() => {
    setRelatedItems([])
    setRelatedError(null)
    setRelatedHasMore(false)
  }, [file.id])

  const loadFileLinks = React.useCallback(async () => {
    try {
      const data = await apiService.getFileLinks(file.id)
      setFileLinks(data)
    } catch {
      setFileLinks({ outgoing: [], incoming: [] })
    }
  }, [file.id])

  React.useEffect(() => {
    void loadFileLinks()
  }, [loadFileLinks])

  const RELATED_PAGE_SIZE = 10

  const loadRelatedDocuments = React.useCallback(async () => {
    setRelatedLoading(true)
    setRelatedError(null)
    try {
      const data = await apiService.getRelatedFiles(file.id, {
        offset: 0,
        limit: RELATED_PAGE_SIZE,
      })
      setRelatedItems(data.related)
      setRelatedHasMore(Boolean(data.has_more))
    } catch {
      setRelatedError(t('filePreview.related.error'))
      setRelatedItems([])
      setRelatedHasMore(false)
    } finally {
      setRelatedLoading(false)
    }
  }, [file.id, t])

  const loadMoreRelatedDocuments = async () => {
    if (relatedLoadingMore || !relatedHasMore) return
    setRelatedLoadingMore(true)
    try {
      const data = await apiService.getRelatedFiles(file.id, {
        offset: relatedItems.length,
        limit: RELATED_PAGE_SIZE,
      })
      setRelatedItems((prev) => {
        const seen = new Set(prev.map((r) => r.file.id))
        const merged = [...prev]
        for (const row of data.related) {
          if (!seen.has(row.file.id)) {
            seen.add(row.file.id)
            merged.push(row)
          }
        }
        return merged
      })
      setRelatedHasMore(Boolean(data.has_more))
    } catch {
      toast({
        title: t('filePreview.related.error'),
        status: 'error',
        duration: 4000,
        isClosable: true,
      })
    } finally {
      setRelatedLoadingMore(false)
    }
  }

  const openRelatedDocuments = () => {
    onRelatedOpenBase()
    void loadRelatedDocuments()
  }

  const navigateToRelatedFile = React.useCallback(
    (targetFileId: number) => {
      const qs = searchParams.toString()
      navigate(qs ? `/preview/${targetFileId}?${qs}` : `/preview/${targetFileId}`)
      onRelatedClose()
    },
    [navigate, onRelatedClose, searchParams]
  )

  const handleLinkFromRelated = React.useCallback(
    async (targetId: number) => {
      if (targetId === file.id || linkedPeerFileIds.has(targetId)) {
        return
      }
      setLinkingRelatedId(targetId)
      try {
        await apiService.createFileLink(file.id, targetId)
        toast({
          title: t('filePreview.linkDocuments.success'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
        await loadFileLinks()
      } catch {
        toast({
          title: t('filePreview.linkDocuments.error'),
          status: 'error',
          duration: 4000,
          isClosable: true,
        })
      } finally {
        setLinkingRelatedId(null)
      }
    },
    [file.id, linkedPeerFileIds, loadFileLinks, t, toast]
  )

  const isPDF = file.mime_type === 'application/pdf'
  const isImage = file.mime_type?.startsWith('image/')
  const isSpreadsheet =
    file.mime_type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    file.mime_type === 'application/vnd.ms-excel'
  const isDocx =
    file.mime_type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    file.mime_type === 'application/msword'

  return (
    <Flex direction="column" h="100%" bg="white" overflow="hidden">
      <PreviewTopBar
        file={file}
        scale={scale}
        onScaleChange={setScale}
        onBack={handleBack}
        onTagsClick={onTagModalOpen}
        onRelatedDocumentsClick={openRelatedDocuments}
        relatedDocumentsDisabled={file.embedding_status !== 'completed'}
        onLinkDocumentClick={onLinkOpen}
        onFileRenamed={onFileRenamed}
      />

      <Flex flex={1} overflow="hidden">
        {/* Document viewer */}
        <Box flex={1} overflow="hidden" bg="gray.50" position="relative" display="flex" flexDirection="column">
          {/* Floating view mode toggle */}
          <ButtonGroup position="absolute" top={1.5} right={1.5} size="sm" isAttached zIndex={10} boxShadow="md" borderRadius="md">
            <Tooltip label={t('filePreview.viewModes.document')} placement="bottom">
              <IconButton
                aria-label={t('filePreview.viewModes.document')}
                icon={<FiFileText />}
                onClick={() => setViewMode('document')}
                colorScheme={viewMode === 'document' ? 'brand' : 'gray'}
                variant={viewMode === 'document' ? 'solid' : 'outline'}
                bg={viewMode === 'document' ? undefined : 'white'}
              />
            </Tooltip>
            <Tooltip label={t('filePreview.viewModes.text')} placement="bottom">
              <IconButton
                aria-label={t('filePreview.viewModes.text')}
                icon={<FiAlignLeft />}
                onClick={() => setViewMode('text')}
                colorScheme={viewMode === 'text' ? 'brand' : 'gray'}
                variant={viewMode === 'text' ? 'solid' : 'outline'}
                bg={viewMode === 'text' ? undefined : 'white'}
              />
            </Tooltip>
            <Tooltip label={t('filePreview.viewModes.graph')} placement="bottom">
              <IconButton
                aria-label={t('filePreview.viewModes.graph')}
                icon={<FiShare2 />}
                onClick={() => setViewMode('graph')}
                colorScheme={viewMode === 'graph' ? 'brand' : 'gray'}
                variant={viewMode === 'graph' ? 'solid' : 'outline'}
                bg={viewMode === 'graph' ? undefined : 'white'}
              />
            </Tooltip>
          </ButtonGroup>

          {viewMode === 'document' ? (
            isPDF ? (
              <PDFViewer file={file} scale={scale} onScaleChange={setScale} />
            ) : isImage ? (
              <ImageViewer file={file} scale={scale} onScaleChange={setScale} />
            ) : isSpreadsheet ? (
              <SpreadsheetViewer file={file} scale={scale} onScaleChange={setScale} />
            ) : isDocx ? (
              <DocxViewer file={file} scale={scale} onScaleChange={setScale} />
            ) : (
              <Center h="full">
                <Alert status="info" borderRadius="lg" maxW="md">
                  <AlertIcon />
                  {t('filePreview.messages.previewNotAvailable')}
                </Alert>
              </Center>
            )
          ) : viewMode === 'text' ? (
            <Box p={4} pt={10} flex={1} overflow="hidden" display="flex" flexDirection="column">
              <TextSearchViewer text={file.extracted_text ?? ''} />
            </Box>
          ) : (
            <Box flex={1} minH={0} overflow="hidden" display="flex" flexDirection="column">
              <LinkGraphView
                file={file}
                relatedItems={relatedItems}
                relatedLoading={relatedLoading}
                linkingRelatedId={linkingRelatedId}
                onLinkFromRelated={handleLinkFromRelated}
                onOpenLinkModal={onLinkOpen}
                onNavigate={navigateToRelatedFile}
                onFileLinksChanged={loadFileLinks}
                onLoadRelated={loadRelatedDocuments}
              />
            </Box>
          )}
        </Box>

        {/* AI Sidebar */}
        {!isMobile && (
          <PreviewSidebar
            file={file}
            tags={localTags}
            summaries={file.summaries || []}
            fileLinksOutgoing={fileLinks.outgoing}
            fileLinksIncoming={fileLinks.incoming}
            onFileLinksChanged={loadFileLinks}
          />
        )}
      </Flex>

      <Modal isOpen={isTagModalOpen} onClose={onTagModalClose} size="lg">
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
        <ModalContent borderRadius="xl">
          <ModalHeader>Tags</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <TagManager
              file={file}
              tags={localTags}
              onTagAdded={handleTagAdded}
              onTagRemoved={handleTagRemoved}
            />
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal isOpen={isRelatedOpen} onClose={onRelatedClose} size="lg">
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
        <ModalContent borderRadius="xl" maxH="85vh">
          <ModalHeader>{t('filePreview.related.modalTitle')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} overflowY="auto">
            {relatedLoading && (
              <Flex align="center" justify="center" py={8} gap={3}>
                <Spinner color="brand.500" />
                <Text color="gray.600">{t('filePreview.related.loading')}</Text>
              </Flex>
            )}
            {!relatedLoading && relatedError && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                {relatedError}
              </Alert>
            )}
            {!relatedLoading && !relatedError && relatedItems.length === 0 && (
              <Text color="gray.600">{t('filePreview.related.empty')}</Text>
            )}
            {!relatedLoading && !relatedError && relatedItems.length > 0 && (
              <VStack align="stretch" spacing={3}>
                {relatedItems.map((r) => {
                  const name = r.file.display_name || r.file.original_name || r.file.name
                  const isPeerLinked = linkedPeerFileIds.has(r.file.id)
                  const isRowLinking = linkingRelatedId === r.file.id
                  return (
                    <Box
                      key={r.file.id}
                      p={3}
                      borderRadius="md"
                      borderWidth="1px"
                      borderColor="gray.200"
                      _hover={{ bg: 'gray.50' }}
                    >
                      <HStack justify="space-between" align="flex-start" spacing={3}>
                        <Box flex={1} minW={0}>
                          <Text fontWeight="600" noOfLines={1}>
                            {name}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            {t('filePreview.related.relevance')}: {formatRelevanceScore(r.score)}
                          </Text>
                          {r.match_snippet && (
                            <Text fontSize="sm" color="gray.600" noOfLines={3} mt={1}>
                              {r.match_snippet}
                            </Text>
                          )}
                        </Box>
                        <HStack flexShrink={0} spacing={2}>
                          <Button
                            size="sm"
                            variant="outline"
                            isDisabled={isRowLinking || relatedLoadingMore}
                            onClick={() => navigateToRelatedFile(r.file.id)}
                          >
                            {t('filePreview.related.open')}
                          </Button>
                          {isPeerLinked ? (
                            <Text fontSize="sm" color="gray.500" whiteSpace="nowrap" py={1}>
                              {t('filePreview.related.linked')}
                            </Text>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              colorScheme="brand"
                              isDisabled={isRowLinking || relatedLoadingMore}
                              onClick={() => void handleLinkFromRelated(r.file.id)}
                            >
                              {isRowLinking ? <Spinner size="sm" color="brand.500" /> : t('filePreview.related.link')}
                            </Button>
                          )}
                        </HStack>
                      </HStack>
                    </Box>
                  )
                })}
                {relatedHasMore && (
                  <Button
                    variant="outline"
                    colorScheme="brand"
                    isLoading={relatedLoadingMore}
                    loadingText={t('filePreview.related.loading')}
                    onClick={() => void loadMoreRelatedDocuments()}
                  >
                    {t('filePreview.related.loadMore')}
                  </Button>
                )}
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      <LinkDocumentModal
        isOpen={isLinkOpen}
        onClose={onLinkClose}
        sourceFileId={file.id}
        onLinked={loadFileLinks}
      />
    </Flex>
  )
}
